<div class="space-y-6">

  <!-- ── 1. Stat Tiles ── -->
  <!-- grid-cols-2 at md keeps tiles wide enough so long titles don't wrap;
       xl:grid-cols-4 expands to 4-up only on large screens -->
  <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

    <!-- Total Webhooks -->
    <div hlmCard size="sm">
      <div hlmCardHeader class="pb-2">
        <div class="flex flex-row items-center justify-between space-y-0">
          <h3 class="tracking-tight text-sm font-medium text-muted-foreground truncate">Total Webhooks (24h)</h3>
          <ng-icon hlm name="lucideActivity" class="size-4 text-muted-foreground shrink-0 ml-2" />
        </div>
      </div>
      <div hlmCardContent>
        @if (healthSummaryLoading()) {
          <hlm-skeleton class="h-8 w-20 mb-1" />
          <hlm-skeleton class="h-3 w-32" />
        } @else {
          <div class="text-2xl font-bold">{{ statTotalWebhooks() | number }}</div>
          <p class="text-xs text-muted-foreground">Across all integrations</p>
        }
      </div>
    </div>

    <!-- Success Rate -->
    <div hlmCard size="sm">
      <div hlmCardHeader class="pb-2">
        <div class="flex flex-row items-center justify-between space-y-0">
          <h3 class="tracking-tight text-sm font-medium text-muted-foreground truncate">Success Rate</h3>
          <ng-icon hlm name="lucideCheckCircle2" class="size-4 text-muted-foreground shrink-0 ml-2" />
        </div>
      </div>
      <div hlmCardContent>
        @if (healthSummaryLoading()) {
          <hlm-skeleton class="h-8 w-20 mb-1" />
          <hlm-skeleton class="h-3 w-28" />
        } @else {
          <div class="text-2xl font-bold">{{ statSuccessRate() | number:'1.0-1' }}%</div>
          <p class="text-xs text-muted-foreground">Weighted avg last 24h</p>
        }
      </div>
    </div>

    <!-- Failed Calls -->
    <div hlmCard size="sm">
      <div hlmCardHeader class="pb-2">
        <div class="flex flex-row items-center justify-between space-y-0">
          <h3 class="tracking-tight text-sm font-medium text-muted-foreground truncate">Failed Calls</h3>
          <ng-icon hlm name="lucideAlertTriangle" class="size-4 text-muted-foreground shrink-0 ml-2" />
        </div>
      </div>
      <div hlmCardContent>
        @if (healthSummaryLoading()) {
          <hlm-skeleton class="h-8 w-16 mb-1" />
          <hlm-skeleton class="h-3 w-36" />
        } @else {
          <div class="text-2xl font-bold"
            [class.text-destructive]="statFailedCalls() > 0">
            {{ statFailedCalls() | number }}
          </div>
          <p class="text-xs text-muted-foreground">Estimated 4xx + 5xx last 24h</p>
        }
      </div>
    </div>

    <!-- Avg Response Time -->
    <div hlmCard size="sm">
      <div hlmCardHeader class="pb-2">
        <div class="flex flex-row items-center justify-between space-y-0">
          <h3 class="tracking-tight text-sm font-medium text-muted-foreground truncate">Avg Response Time</h3>
          <ng-icon hlm name="lucideClock" class="size-4 text-muted-foreground shrink-0 ml-2" />
        </div>
      </div>
      <div hlmCardContent>
        @if (healthSummaryLoading()) {
          <hlm-skeleton class="h-8 w-20 mb-1" />
          <hlm-skeleton class="h-3 w-24" />
        } @else {
          <div class="text-2xl font-bold">{{ statAvgResponseMs() | number }}</div>
          <p class="text-xs text-muted-foreground">Milliseconds per request</p>
        }
      </div>
    </div>

  </div>

  <!-- ── 2. Integration Health Cards ── -->
  <div>
    <div class="mb-4">
      <h2 class="text-lg font-semibold tracking-tight">Integration Status</h2>
      <p class="text-sm text-muted-foreground">Health summary for every configured integration.</p>
    </div>

    @if (healthSummaryLoading()) {
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (_ of [1,2,3]; track $index) {
          <div hlmCard class="p-4">
            <hlm-skeleton class="h-4 w-24 mb-3" />
            <hlm-skeleton class="h-6 w-16 mb-2" />
            <hlm-skeleton class="h-3 w-full" />
          </div>
        }
      </div>
    } @else if (healthSummary().length === 0) {
      <div hlmCard size="sm">
        <div hlmCardContent class="py-12">
          <div class="flex flex-col items-center justify-center text-center">
            <ng-icon hlm name="lucideInbox" class="size-10 text-muted-foreground mb-3" />
            <h3 class="text-lg font-semibold">No integration data yet</h3>
            <p class="text-sm text-muted-foreground max-w-sm mt-1">
              Health cards will appear here once webhooks have been received.
            </p>
          </div>
        </div>
      </div>
    } @else {
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        @for (integration of healthSummary(); track integration.source) {
          <div hlmCard size="sm">
            <div hlmCardHeader class="pb-3">
              <div class="flex items-center justify-between">
                <h3 hlmCardTitle class="text-base">{{ integration.source | titlecase }}</h3>
                <span hlmBadge [variant]="getHealthBadgeVariant(integration.status)">
                  {{ integration.status | titlecase }}
                </span>
              </div>
            </div>
            <div hlmCardContent class="pt-0">
              <div class="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p class="text-lg font-bold">{{ integration.successRate24h | number:'1.0-1' }}%</p>
                  <p class="text-xs text-muted-foreground">Success rate</p>
                </div>
                <div>
                  <p class="text-lg font-bold">{{ integration.avgResponseTimeMs }}</p>
                  <p class="text-xs text-muted-foreground">Avg ms</p>
                </div>
                <div>
                  <p class="text-lg font-bold">{{ integration.totalRequests24h }}</p>
                  <p class="text-xs text-muted-foreground">Requests</p>
                </div>
              </div>
              @if (integration.lastWebhookTimestamp) {
                <p class="text-xs text-muted-foreground mt-3 border-t border-border pt-2">
                  Last call: {{ integration.lastWebhookTimestamp | date:'short' }}
                </p>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>

  <!-- ── 3. Webhook Log Table ── -->
  <div hlmCard>
    <div hlmCardHeader>
      <div class="flex items-center justify-between">
        <div>
          <h3 hlmCardTitle>Webhook Logs</h3>
          <p hlmCardDescription>Recent inbound requests — click a row to inspect bodies</p>
        </div>
        <button hlmBtn variant="outline" size="sm" (click)="loadWebhookLogs()" aria-label="Refresh logs">
          <ng-icon hlm name="lucideRefreshCw" class="size-4" />
        </button>
      </div>
    </div>
    <div hlmCardContent>

      <!-- Filter bar -->
      <div class="grid grid-cols-2 gap-3 md:grid-cols-5 mb-4 items-end">
        <div>
          <label hlmLabel class="mb-1 block">Integration</label>
          <select class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            [(ngModel)]="webhookFilter.source">
            <option value="">All Sources</option>
            <option value="shopee">Shopee</option>
          </select>
        </div>
        <div>
          <label hlmLabel class="mb-1 block">Status</label>
          <select class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            [(ngModel)]="webhookFilter.status">
            <option value="">All Statuses</option>
            <option value="2xx">2xx Success</option>
            <option value="4xx">4xx Client Error</option>
            <option value="5xx">5xx Server Error</option>
          </select>
        </div>
        <div>
          <label hlmLabel class="mb-1 block">From</label>
          <input hlmInput class="w-full" type="datetime-local" [(ngModel)]="webhookFilter.from" />
        </div>
        <div>
          <label hlmLabel class="mb-1 block">To</label>
          <input hlmInput class="w-full" type="datetime-local" [(ngModel)]="webhookFilter.to" />
        </div>
        <div class="flex gap-2">
          <button hlmBtn size="sm" (click)="applyWebhookFilters()">Apply</button>
          <button hlmBtn variant="outline" size="sm" (click)="resetWebhookFilters()">Reset</button>
        </div>
      </div>

      <!-- Table -->
      <div class="overflow-auto rounded-md border border-border">
        <table hlmTable class="w-full">
          <thead hlmTHead class="sticky top-0 z-10 bg-background">
            <tr hlmTr>
              <th hlmTh class="w-36">Timestamp</th>
              <th hlmTh class="w-16">Method</th>
              <th hlmTh>Path</th>
              <th hlmTh class="w-20">Status</th>
              <th hlmTh class="w-24">Response (ms)</th>
              <th hlmTh class="w-24">Source</th>
              <th hlmTh class="w-48">Error</th>
              <th hlmTh class="w-12"></th>
            </tr>
          </thead>
          <tbody hlmTBody>
            @if (webhookLogsLoading()) {
              @for (_ of [1,2,3,4,5]; track $index) {
                <tr hlmTr>
                  <td hlmTd colspan="8" class="py-1">
                    <hlm-skeleton class="h-6 w-full" />
                  </td>
                </tr>
              }
            } @else if (webhookLogs().length === 0) {
              <tr hlmTr>
                <td hlmTd colspan="8">
                  <div class="flex flex-col items-center justify-center py-12 text-center">
                    <ng-icon hlm name="lucideInbox" class="size-10 text-muted-foreground mb-3" />
                    <h3 class="text-lg font-semibold">No webhook logs found</h3>
                    <p class="text-sm text-muted-foreground max-w-sm mt-1">
                      Adjust the filters or wait for incoming webhooks.
                    </p>
                  </div>
                </td>
              </tr>
            } @else {
              @for (log of webhookLogs(); track log.id) {
                <tr hlmTr class="cursor-pointer hover:bg-muted/50" (click)="openLogDetail(log)">
                  <td hlmTd class="text-xs">{{ log.timestamp | date:'short' }}</td>
                  <td hlmTd class="text-xs font-mono">{{ log.method }}</td>
                  <td hlmTd class="text-xs font-mono truncate max-w-[200px]" [title]="log.path">{{ log.path }}</td>
                  <td hlmTd>
                    <span hlmBadge [variant]="getStatusBadgeVariant(log.statusCode)">{{ log.statusCode }}</span>
                  </td>
                  <td hlmTd class="text-xs tabular-nums">{{ log.responseTimeMs }}ms</td>
                  <td hlmTd class="text-xs">{{ log.source }}</td>
                  <td hlmTd class="text-xs text-muted-foreground truncate max-w-[180px]" [title]="log.errorMessage ?? ''">
                    {{ log.errorMessage ?? '—' }}
                  </td>
                  <td hlmTd>
                    <button hlmBtn variant="ghost" size="sm"
                      (click)="openLogDetail(log); $event.stopPropagation()"
                      aria-label="View details">
                      <ng-icon hlm name="lucideExternalLink" class="size-3.5" />
                    </button>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      @if (totalPages() > 1) {
        <div class="flex items-center justify-between mt-3 text-sm text-muted-foreground">
          <span>Showing {{ (page() - 1) * pageSize + 1 }}–{{ pageEnd() }} of {{ totalCount() }}</span>
          <div class="flex gap-1">
            <button hlmBtn variant="outline" size="sm" [disabled]="page() === 1" (click)="setPage(page() - 1)">Previous</button>
            @for (p of pageNumbers(); track p) {
              <button hlmBtn [variant]="page() === p ? 'default' : 'outline'" size="sm" (click)="setPage(p)">{{ p }}</button>
            }
            <button hlmBtn variant="outline" size="sm" [disabled]="page() === totalPages()" (click)="setPage(page() + 1)">Next</button>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- ── 4. Order Timeline ── -->
  <div hlmCard>
    <div hlmCardHeader>
      <h3 hlmCardTitle>Order Timeline</h3>
      <p hlmCardDescription>Trace a Shopee order through all system events</p>
    </div>
    <div hlmCardContent>
      <!-- Search bar -->
      <div class="flex gap-2 mb-6">
        <div class="relative flex-1 max-w-xs">
          <ng-icon hlm name="lucideSearch"
            class="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <input hlmInput
            class="w-full pl-9"
            type="text"
            placeholder="Enter Shopee Order ID"
            [ngModel]="timelineSearchInput()"
            (ngModelChange)="timelineSearchInput.set($event)"
            (keyup.enter)="searchTimeline()" />
        </div>
        <button hlmBtn size="sm" (click)="searchTimeline()" [disabled]="timelineLoading()">
          @if (timelineLoading()) {
            <ng-icon hlm name="lucideRefreshCw" class="size-4 animate-spin mr-1" />
          }
          Search
        </button>
        @if (timelineOrderId()) {
          <button hlmBtn variant="outline" size="sm" (click)="clearTimeline()">Clear</button>
        }
      </div>

      <!-- Timeline states -->
      @if (timelineLoading()) {
        <div class="space-y-4">
          @for (_ of [1,2,3]; track $index) {
            <div class="flex gap-3 pl-6">
              <hlm-skeleton class="h-3 w-3 rounded-full shrink-0 mt-1" />
              <div class="flex-1 space-y-1.5">
                <hlm-skeleton class="h-3 w-32" />
                <hlm-skeleton class="h-3 w-full" />
              </div>
            </div>
          }
        </div>
      } @else if (timelineError()) {
        <p class="text-sm text-destructive">{{ timelineError() }}</p>
      } @else if (timelineEvents().length > 0) {
        <div class="relative">
          <!-- Vertical connector line -->
          <div class="absolute left-[5px] top-2 bottom-2 w-0.5 bg-border"></div>
          <ol class="space-y-5 relative">
            @for (event of timelineEvents(); track $index) {
              <li class="flex gap-4 pl-6 relative">
                <span class="absolute left-0 top-1.5 h-3 w-3 rounded-full border-2 border-background shadow-sm {{ getTimelineEventColor(event.eventType) }}"></span>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-0.5">
                    <span class="text-xs font-semibold">{{ event.eventType }}</span>
                    <span hlmBadge variant="outline" class="text-[10px] px-1 py-0">{{ event.source }}</span>
                  </div>
                  <p class="text-xs text-muted-foreground">{{ event.timestamp | date:'medium' }}</p>
                  <p class="text-xs mt-0.5">{{ event.details }}</p>
                </div>
              </li>
            }
          </ol>
        </div>
      } @else if (timelineOrderId()) {
        <p class="text-sm text-muted-foreground">
          No events found for order <code class="bg-muted px-1 rounded">{{ timelineOrderId() }}</code>.
        </p>
      } @else {
        <div class="flex flex-col items-center justify-center py-10 text-center">
          <ng-icon hlm name="lucideSearch" class="size-8 text-muted-foreground mb-3" />
          <p class="text-sm text-muted-foreground">Enter an order ID above to view its lifecycle events.</p>
        </div>
      }
    </div>
  </div>

  <!-- ── 5. Error Patterns ── -->
  <div hlmCard>
    <div hlmCardHeader>
      <div class="flex items-center justify-between">
        <div>
          <h3 hlmCardTitle>Error Patterns</h3>
          <p hlmCardDescription>Recurring errors grouped by endpoint and status code</p>
        </div>
        <button hlmBtn variant="outline" size="sm" (click)="loadErrorPatterns()" aria-label="Refresh error patterns">
          <ng-icon hlm name="lucideRefreshCw" class="size-4" />
        </button>
      </div>
    </div>
    <div hlmCardContent>
      @if (errorPatternsLoading()) {
        <div class="space-y-2">
          @for (_ of [1,2,3,4]; track $index) {
            <hlm-skeleton class="h-8 w-full" />
          }
        </div>
      } @else if (errorPatterns().length === 0) {
        <div class="flex flex-col items-center justify-center py-10 text-center">
          <ng-icon hlm name="lucideCheckCircle2" class="size-8 text-muted-foreground mb-3" />
          <h3 class="text-base font-semibold">No error patterns detected</h3>
          <p class="text-sm text-muted-foreground mt-1">All requests are succeeding.</p>
        </div>
      } @else {
        <div class="overflow-auto rounded-md border border-border">
          <table hlmTable class="w-full">
            <thead hlmTHead>
              <tr hlmTr>
                <th hlmTh>Endpoint</th>
                <th hlmTh class="w-24">Status</th>
                <th hlmTh class="w-20 text-right">Count</th>
                <th hlmTh class="w-36">Last Seen</th>
                <th hlmTh>Sample Error</th>
              </tr>
            </thead>
            <tbody hlmTBody>
              @for (pattern of errorPatterns(); track $index) {
                <tr hlmTr>
                  <td hlmTd class="text-xs font-mono">{{ pattern.endpoint }}</td>
                  <td hlmTd>
                    <span hlmBadge [variant]="getStatusBadgeVariant(pattern.statusCode)">{{ pattern.statusCode }}</span>
                  </td>
                  <td hlmTd class="text-sm font-semibold text-right tabular-nums">{{ pattern.count }}</td>
                  <td hlmTd class="text-xs">{{ pattern.lastOccurrence | date:'short' }}</td>
                  <td hlmTd class="text-xs text-muted-foreground truncate max-w-xs" [title]="pattern.sampleMessage ?? ''">
                    {{ pattern.sampleMessage ?? '—' }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>

</div>

<!-- ── Webhook Detail Dialog ── -->
@if (selectedLog()) {
  <brn-dialog [state]="logDetailOpen() ? 'open' : 'closed'" (stateChanged)="logDetailOpen.set($event === 'open')">
    <div hlmDialogOverlay></div>
    <hlm-dialog-content class="max-w-3xl max-h-[85vh] overflow-y-auto">
      <hlm-dialog-header>
        <h3 hlmDialogTitle>Webhook Log #{{ selectedLog()!.id }}</h3>
        <p hlmDialogDescription>
          <span class="font-mono text-xs">{{ selectedLog()!.method }} {{ selectedLog()!.path }}</span>
          &nbsp;
          <span hlmBadge [variant]="getStatusBadgeVariant(selectedLog()!.statusCode)">{{ selectedLog()!.statusCode }}</span>
        </p>
      </hlm-dialog-header>

      @if (logDetailLoading()) {
        <div class="space-y-2 my-4">
          <hlm-skeleton class="h-4 w-full" />
          <hlm-skeleton class="h-4 w-3/4" />
          <hlm-skeleton class="h-32 w-full" />
        </div>
      } @else {
        <div class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm my-4">
          <span class="text-muted-foreground font-medium">Timestamp:</span>
          <span>{{ selectedLog()!.timestamp | date:'full' }}</span>

          <span class="text-muted-foreground font-medium">Source:</span>
          <span>{{ selectedLog()!.source }}</span>

          <span class="text-muted-foreground font-medium">Response Time:</span>
          <span class="tabular-nums">{{ selectedLog()!.responseTimeMs }}ms</span>

          @if (selectedLog()!.uniqueTrackingId) {
            <span class="text-muted-foreground font-medium">Tracking ID:</span>
            <code class="text-xs bg-muted px-1 py-0.5 rounded">{{ selectedLog()!.uniqueTrackingId }}</code>
          }
        </div>

        @if (selectedLog()!.requestBody) {
          <div hlmCard class="mb-3">
            <div class="px-4 pt-4 pb-2">
              <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request Body</p>
            </div>
            <div class="px-4 pb-4 max-h-48 overflow-y-auto">
              <pre class="text-xs whitespace-pre-wrap break-all">{{ tryFormatJson(selectedLog()!.requestBody) }}</pre>
            </div>
          </div>
        }

        @if (selectedLog()!.responseBody) {
          <div hlmCard class="mb-3">
            <div class="px-4 pt-4 pb-2">
              <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Response Body</p>
            </div>
            <div class="px-4 pb-4 max-h-48 overflow-y-auto">
              <pre class="text-xs whitespace-pre-wrap break-all">{{ tryFormatJson(selectedLog()!.responseBody) }}</pre>
            </div>
          </div>
        }

        @if (selectedLog()!.errorMessage) {
          <div hlmCard size="sm">
            <div class="px-4 pt-4 pb-2">
              <p class="text-xs font-semibold uppercase tracking-wide text-destructive">Error</p>
            </div>
            <div class="px-4 pb-4">
              <pre class="text-xs whitespace-pre-wrap break-all text-destructive">{{ selectedLog()!.errorMessage }}</pre>
            </div>
          </div>
        }
      }

      <hlm-dialog-footer class="mt-4">
        <button hlmBtn variant="outline" (click)="closeLogDetail()">Close</button>
      </hlm-dialog-footer>
    </hlm-dialog-content>
  </brn-dialog>
}
